import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Snackbar,
  Alert,
  IconButton,
  Typography,
  Box,
  Autocomplete,
} from "@mui/material";
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../theme";
import categoryService from "../../repository/categoryRepository";

const AddCategory = ({ open, onClose, onSave }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [mainCategories, setMainCategories] = useState([]);
  const [formData, setFormData] = useState({
    mainCategoryId: "",
    mainCategoryName: "",
    categoryName: "",
    description: "",
    categoryIcon: null,
    categoryIconName: "",
  });
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [error, setError] = useState("");

  const fetchMainCategories = useCallback(async () => {
    try {
      const response = await categoryService.getMainCategories();
      if (response && Array.isArray(response.data)) {
        setMainCategories(response.data);
      } else {
        throw new Error("Invalid data format for main categories");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to fetch main categories");
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchMainCategories();
    }
  }, [open, fetchMainCategories]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleIconUpload = (event) => {
    const file = event.target.files[0];
    setFormData((prev) => ({
      ...prev,
      categoryIcon: file,
      categoryIconName: file ? file.name : "",
    }));
  };

  const handleSubmit = async () => {
    try {
      // const apiEndpoint =
      //   "https://backend.hindustanwardrobe.in/admin/addCategory";
      const data = new FormData();
      const {
        mainCategoryId,
        mainCategoryName,
        categoryName,
        description,
        categoryIcon,
      } = formData;

      data.append("mainCategoryId", mainCategoryId);
      data.append("mainCategoryName", mainCategoryName);
      data.append("categoryName", categoryName);
      data.append("description", description);

      if (categoryIcon) {
        data.append("imageURL", categoryIcon);
      }

      const response = await categoryService.addCategory(data);

      if (response.success) {
        setSnackbarMessage("Category added successfully!");
        setSnackbarSeverity("success");
        if (onSave && typeof onSave === "function") {
          onSave(response.data);
        }
        onClose();
      } else {
        setSnackbarMessage("Error adding category.");
        setSnackbarSeverity("error");
      }
    } catch (error) {
      setSnackbarMessage("Error: " + error.message);
      setSnackbarSeverity("error");
    } finally {
      setOpenSnackbar(true);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            backgroundColor:
              colors.background || theme.palette.background.default,
            color: colors.text || theme.palette.text.primary,
          },
        }}
      >
        <DialogTitle>
          Add Category
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {error && <Typography color="error">{error}</Typography>}
          <Autocomplete
            options={mainCategories}
            getOptionLabel={(option) => option.mainCategoryName || ""}
            onChange={(event, newValue) =>
              setFormData((prevState) => ({
                ...prevState,
                mainCategoryId: newValue ? newValue.mainCategoryId : "",
                mainCategoryName: newValue ? newValue.mainCategoryName : "",
              }))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Main Category"
                variant="outlined"
                sx={{ mb: "10px" }}
              />
            )}
          />
          <TextField
            fullWidth
            name="categoryName"
            label="Category Name"
            value={formData.categoryName}
            onChange={handleInputChange}
            sx={{ mb: "10px" }}
          />
          <TextField
            fullWidth
            name="description"
            label="Description"
            value={formData.description}
            onChange={handleInputChange}
            sx={{ mb: "10px" }}
          />
          <Box display="flex" flexDirection="column" gap="10px" mt="10px">
            <Box display="flex" alignItems="center" gap="10px">
              <Button
                variant="contained"
                component="label"
                color="secondary"
                startIcon={<CloudUploadIcon />}
                sx={{ mb: "10px" }}
              >
                Upload Icon
                <input
                  type="file"
                  name="categoryIcon"
                  hidden
                  onChange={handleIconUpload}
                />
              </Button>
              {formData.categoryIconName && (
                <Typography variant="body2">
                  Uploaded: {formData.categoryIconName}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="secondary" onClick={handleSubmit}>
            Add Category
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddCategory;
